import React from 'react';
import { func } from 'prop-types';
import { useFormState } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import {
  MAX_PACKAGES,
  PACKAGE_DESCRIPTION_MAX_LENGTH,
  PACKAGE_TITLE_MAX_LENGTH,
  PRICING_MODEL_HOURLY,
  PRICING_MODELS,
  hourlyTakenByOther,
  priceUnitMessageId,
  toEnquiryPackage,
} from '../../util/listingPackages';
import { isPackageValid } from '../../util/listingCompleteness';
import {
  composeValidators,
  maxLength,
  required,
  requiredRichText,
  richTextToText,
} from '../../util/validators';
import {
  EditableItemList,
  FieldCurrencyInput,
  FieldSelect,
  FieldTextEditor,
  FieldTextInput,
  PackagePrice,
  PricingModelBadge,
} from '../../components';
import { stateMessages } from '../EditableItemList/EditableItemList';

import css from './EditListingPackagesSection.css';

const { Money } = sdkTypes;

const NAME = 'packages';
const DRAFT_NAME = 'packageDraft';

export const EMPTY_PACKAGE = { pricingModel: '', price: null, title: '', description: '' };

const MODEL_MESSAGE_IDS = {
  hourly: 'ListingPackages.badgeHourly',
  monthly: 'ListingPackages.badgeMonthly',
  project: 'ListingPackages.badgeProject',
};

// The price input shows a plain number ("2400"), with the currency and the
// unit as a suffix inside the field, so it formats as a decimal.
const PRICE_INPUT_CONFIG = {
  style: 'decimal',
  currency: config.currency,
  useGrouping: false,
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

// Packages store the price as an integer in minor units; FieldCurrencyInput
// works with Money.
const formatPrice = value => (Number.isInteger(value) ? new Money(value, config.currency) : '');
const parsePrice = money => (money instanceof Money ? money.amount : null);

const currencySymbol = intl =>
  intl
    .formatNumber(0, {
      style: 'currency',
      currency: config.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/[\d\s.,]/g, '');

const PackageSummary = ({ item }) => {
  const isPriced = PRICING_MODELS.includes(item.pricingModel) && Number.isInteger(item.price);
  const excerpt = richTextToText(item.description).trim();
  return (
    <div className={css.summary}>
      {item.title ? <span className={css.summaryTitle}>{item.title}</span> : null}
      {isPriced ? (
        <span className={css.summaryPricing}>
          <PricingModelBadge pricingModel={item.pricingModel} />
          <span className={css.summaryPrice}>
            <PackagePrice price={toEnquiryPackage(item).price} pricingModel={item.pricingModel} />
          </span>
        </span>
      ) : null}
      {excerpt ? <span className={css.summaryDescription}>{excerpt}</span> : null}
    </div>
  );
};

const PackageEditorFields = ({ intl, draftName, index, packages, draft }) => {
  const requiredMessage = intl.formatMessage({ id: 'EditListingPackagesSection.required' });
  const tooLongMessage = max =>
    intl.formatMessage({ id: 'EditListingPackagesSection.tooLong' }, { max });
  const priceRequiredMessage = intl.formatMessage({
    id: 'EditListingPackagesSection.priceRequired',
  });
  const priceRequired = value =>
    Number.isInteger(value) && value > 0 ? undefined : priceRequiredMessage;

  const hourlyTaken = hourlyTakenByOther(packages, index);
  const pricingModel = draft && draft.pricingModel;
  const unitMessageId = PRICING_MODELS.includes(pricingModel)
    ? priceUnitMessageId(pricingModel)
    : null;
  const priceSuffix = unitMessageId
    ? `${currencySymbol(intl)} ${intl.formatMessage({ id: unitMessageId })}`
    : currencySymbol(intl);

  const pricingModelId = `${draftName}.pricingModel`;
  const priceId = `${draftName}.price`;
  return (
    <>
      <div className={css.modelAndPrice}>
        <FieldSelect
          className={css.field}
          id={pricingModelId}
          name={pricingModelId}
          label={intl.formatMessage({ id: 'EditListingPackagesSection.pricingModelLabel' })}
          validate={required(requiredMessage)}
          infoText={
            hourlyTaken ? <FormattedMessage id="EditListingPackagesSection.hourlyTaken" /> : null
          }
        >
          <option value="" disabled>
            {intl.formatMessage({ id: 'EditListingPackagesSection.pricingModelPlaceholder' })}
          </option>
          {PRICING_MODELS.map(model => (
            <option
              key={model}
              value={model}
              disabled={model === PRICING_MODEL_HOURLY && hourlyTaken}
            >
              {intl.formatMessage({ id: MODEL_MESSAGE_IDS[model] })}
            </option>
          ))}
        </FieldSelect>
        <div className={css.field}>
          <label htmlFor={priceId}>
            <FormattedMessage id="EditListingPackagesSection.priceLabel" />
          </label>
          <div className={css.priceInput}>
            <FieldCurrencyInput
              id={priceId}
              name={priceId}
              currencyConfig={PRICE_INPUT_CONFIG}
              placeholder={intl.formatMessage({
                id: 'EditListingPackagesSection.pricePlaceholder',
              })}
              format={formatPrice}
              parse={parsePrice}
              validate={priceRequired}
            />
            <span className={css.priceSuffix}>{priceSuffix}</span>
          </div>
        </div>
      </div>
      <FieldTextInput
        id={`${draftName}.title`}
        name={`${draftName}.title`}
        type="text"
        label={intl.formatMessage({ id: 'EditListingPackagesSection.titleLabel' })}
        validate={composeValidators(
          required(requiredMessage),
          maxLength(tooLongMessage(PACKAGE_TITLE_MAX_LENGTH), PACKAGE_TITLE_MAX_LENGTH)
        )}
      />
      <FieldTextEditor
        id={`${draftName}.description`}
        name={`${draftName}.description`}
        type="textarea"
        label={intl.formatMessage({ id: 'EditListingPackagesSection.descriptionLabel' })}
        isCompact
        maxLength={PACKAGE_DESCRIPTION_MAX_LENGTH}
        maxLengthMessage={tooLongMessage(PACKAGE_DESCRIPTION_MAX_LENGTH)}
        validate={requiredRichText(requiredMessage)}
      />
    </>
  );
};

const EditListingPackagesSectionComponent = ({ onPackageSaved, intl }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const packages = values[NAME] || [];
  const draft = values[DRAFT_NAME] || null;
  const readyCount = packages.filter(isPackageValid).length;

  // Figma copy differs between a new package (Draft · Discard · Save package)
  // and a saved one being edited (Changes not saved yet · Cancel · Save changes).
  const message = id => intl.formatMessage({ id: `EditListingPackagesSection.${id}` });
  const messages = {
    newTitle: n => intl.formatMessage({ id: 'EditListingPackagesSection.newPackage' }, { n }),
    editTitle: n => intl.formatMessage({ id: 'EditListingPackagesSection.editPackage' }, { n }),
    ...stateMessages({
      draft,
      itemCount: packages.length,
      newItem: { tag: message('draftTag'), save: message('save'), discard: message('discard') },
      savedItem: {
        tag: message('unsavedTag'),
        save: message('saveChanges'),
        discard: message('cancel'),
      },
      add: message('add'),
      addAnother: message('addAnother'),
    }),
  };

  return (
    <section className={css.root}>
      <div className={css.header}>
        <div className={css.titleRow}>
          <h2 className={css.title}>
            <FormattedMessage id="EditListingPackagesSection.title" />
          </h2>
          <span className={css.readyCount}>
            <FormattedMessage
              id="EditListingPackagesSection.readyCount"
              values={{ count: readyCount }}
            />
          </span>
        </div>
        <p className={css.description}>
          <FormattedMessage id="EditListingPackagesSection.description" />
        </p>
      </div>
      {packages.length > MAX_PACKAGES ? (
        <p className={css.error} role="alert">
          <FormattedMessage
            id="EditListingPackagesSection.tooMany"
            values={{ max: MAX_PACKAGES }}
          />
        </p>
      ) : null}
      <EditableItemList
        name={NAME}
        draftName={DRAFT_NAME}
        maxItems={MAX_PACKAGES}
        emptyItem={EMPTY_PACKAGE}
        isItemValid={isPackageValid}
        renderSummary={item => <PackageSummary item={item} />}
        renderEditorFields={({ draftName, index }) => (
          <PackageEditorFields
            intl={intl}
            draftName={draftName}
            index={index}
            packages={packages}
            draft={draft}
          />
        )}
        messages={messages}
        onSave={onPackageSaved}
      />
    </section>
  );
};

EditListingPackagesSectionComponent.defaultProps = {
  onPackageSaved: null,
};

EditListingPackagesSectionComponent.propTypes = {
  onPackageSaved: func,
  intl: intlShape.isRequired,
};

const EditListingPackagesSection = injectIntl(EditListingPackagesSectionComponent);

export default EditListingPackagesSection;
